<template>
  <main class="main page-inside">
    <div class="main-wrapper">
      <div class="section hero-section">
        <div class="wrapper">
          <div class="content">
            <div class="confirmation-success">
              <div class="confirmation-success__icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="40" cy="40" r="40" fill="#F9F5FD"/>
                  <path d="M54.6667 33L35.5 52.1667L25.3333 42" stroke="#8C44AE" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h1 class="confirmation-success__title">{{ $t(`Transaction Confirmed`) }}</h1>
              
              <!-- Already confirmed message -->
              <p class="confirmation-success__message" v-if="alreadyConfirmed">
                {{ $t(`This transaction has already been confirmed. No further action is needed.`) }}
              </p>
              
              <!-- Default success message -->
              <p class="confirmation-success__message" v-else>
                {{ $t(`Your transaction has been successfully confirmed.`) }}
                <span v-if="!paymentUrl">{{ $t(`Your order is now being processed.`) }}</span>
              </p>
              
              <!-- Payment redirect section (only for fiat-to-crypto) -->
              <div v-if="paymentUrl && !alreadyConfirmed" class="payment-redirect">
                <p class="redirect-message">{{ $t(`Please proceed to payment to complete your transaction.`) }}</p>
                <a :href="paymentUrl" class="button primary payment-button">
                  {{ $t(`Proceed to Payment`) }}
                </a>
                <div class="auto-redirect-message">
                  {{ $t(`Redirecting automatically in`) }} {{ redirectCountdown }} {{ $t(`seconds...`) }}
                </div>
              </div>
              
              <!-- Additional instructions (for both flows when not already confirmed) -->
              <div class="instruction-message" v-if="!alreadyConfirmed">
                {{ $t(`Please return to the original browser tab where you started your transaction. The process will continue automatically there.`) }}
              </div>
              
              <!-- Buttons -->
              <div class="buttons">
                <button class="button secondary" v-if="!paymentUrl" @click="goToHome">{{ $t(`Go to Home`) }}</button>
                <button class="button primary" v-if="!paymentUrl" @click="goToBuyCrypto">{{ $t(`Buy More Crypto`) }}</button>
                <button class="button secondary" v-if="paymentUrl && !alreadyConfirmed" @click="cancelRedirect">{{ $t(`Cancel Redirect`) }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'ConfirmationSuccessPage',
  data() {
    return {
      alreadyConfirmed: false,
      paymentUrl: '',
      redirectCountdown: 5,
      redirectTimer: null,
      orderUuid: ''
    }
  },
  methods: {
    goToHome() {
      this.$router.push({ path: '/' });
    },
    goToBuyCrypto() {
      this.$router.push({ path: '/buy-crypto' });
    },
    goToOrderDetails() {
      // If we have an order UUID, go to order details
      if (this.orderUuid) {
        this.$router.push({ path: '/order-details', query: { order_uuid: this.orderUuid } });
      } else {
        this.$router.push({ path: '/order-details' });
      }
    },
    cancelRedirect() {
      // Stop the redirect countdown
      if (this.redirectTimer) {
        clearInterval(this.redirectTimer);
        this.redirectTimer = null;
      }
      this.redirectCountdown = 0;
    },
    checkOrderStatus() {
      // Get order UUID from the URL
      this.orderUuid = this.$route.params.orderId;
      
      if (!this.orderUuid) return;
      
      // Make API call to check status and get payment URL
      this.$http.post(
        `transaction/check-status`,
        { order_uuid: this.orderUuid }
      ).then(response => {
        if (response.data.status === 'OK') {
          if (response.data.confirmed && response.data.payment_url) {
            this.paymentUrl = response.data.payment_url;
            this.startRedirectCountdown();
          } 
        }
      }).catch(error => {
        console.error('Error checking order status:', error);
      });
    },
    startRedirectCountdown() {
      // Clear any existing timer
      if (this.redirectTimer) {
        clearInterval(this.redirectTimer);
      }
      
      this.redirectTimer = setInterval(() => {
        if (this.redirectCountdown > 0) {
          this.redirectCountdown--;
        } else {
          clearInterval(this.redirectTimer);
          if (this.paymentUrl) {
            window.location.href = this.paymentUrl;
          }
        }
      }, 1000);
    }
  },
  mounted() {
    // Check query parameters
    this.alreadyConfirmed = this.$route.query.already === 'true';
    
    // Check if order_uuid is in the query parameters
    if (this.$route.query.order_uuid) {
      this.orderUuid = this.$route.query.order_uuid;
    }
    
    // If payment URL is passed directly in the URL, use it
    if (this.$route.query.payment_url) {
      this.paymentUrl = decodeURIComponent(this.$route.query.payment_url);
      this.startRedirectCountdown();
    } else {
      // Otherwise check the API for the order status
      this.checkOrderStatus();
    }
  },
  beforeDestroy() {
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
    }
  }
}
</script>

<style scoped>
.page-inside {
  line-height: 1;
}

.confirmation-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: #FFFFFF;
  border-radius: 16px;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.confirmation-success__icon {
  margin-bottom: 24px;
}

.confirmation-success__title {
  font-size: 24px;
  font-weight: 700;
  color: #1E293B;
  margin-bottom: 16px;
}

.confirmation-success__message {
  font-size: 16px;
  color: #64748B;
  margin-bottom: 24px;
  line-height: 1.5;
}

.payment-redirect {
  margin-bottom: 24px;
  width: 100%;
}

.redirect-message {
  font-size: 16px;
  color: #1E293B;
  margin-bottom: 16px;
  font-weight: 500;
}

.payment-button {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  text-decoration: none;
  text-align: center;
}

.auto-redirect-message {
  font-size: 14px;
  color: #64748B;
  margin-top: 8px;
}

.instruction-message {
  font-size: 14px;
  color: #1E293B;
  margin-bottom: 24px;
  padding: 12px 16px;
  background-color: #F9F5FD;
  border-left: 3px solid #8C44AE;
  border-radius: 4px;
  text-align: left;
  width: 100%;
}

.buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  width: 100%;
}

.button {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.button.primary {
  background: #8C44AE;
  color: white;
  border: none;
}

.button.primary:hover {
  background: #7a399a;
}

.button.secondary {
  background: transparent;
  color: #8C44AE;
  border: 1px solid #8C44AE;
}

.button.secondary:hover {
  background: rgba(140, 68, 174, 0.1);
}

@media (max-width: 480px) {
  .confirmation-success {
    padding: 30px 16px;
  }
  
  .confirmation-success__title {
    font-size: 20px;
  }
  
  .confirmation-success__message {
    font-size: 14px;
  }
  
  .instruction-message {
    font-size: 13px;
    padding: 10px 14px;
  }
}
</style> 